import React from 'react';
import { cn } from '@/lib/utils';

export interface PropertyDetailProps {
  /** Icon element (from lucide-react or similar) */
  icon?: React.ReactNode;
  /** The value to display */
  value: React.ReactNode;
  /** Optional label to display after the value */
  label?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  sm: {
    container: 'text-xs',
    icon: 'w-3 h-3',
  },
  md: {
    container: 'text-sm',
    icon: 'w-4 h-4',
  },
  lg: {
    container: 'text-base',
    icon: 'w-5 h-5',
  },
};

/**
 * PropertyDetail - A standardized component for displaying icon+value+label
 * 
 * Features:
 * - Consistent spacing between icon and text
 * - Configurable sizes
 * - Muted foreground color
 * 
 * Common use cases:
 * - Venue properties (rooms, capacity, distance)
 * - User stats
 * - Property attributes
 * 
 * @example
 * ```tsx
 * <PropertyDetail icon={<Building2 />} value={50} label="rooms" />
 * <PropertyDetail icon={<Users />} value={200} label="capacity" />
 * <PropertyDetail icon={<Plane />} value="12.5km" />
 * ```
 */
export function PropertyDetail({
  icon,
  value,
  label,
  size = 'md',
  className,
}: PropertyDetailProps) {
  const classes = sizeClasses[size];

  return (
    <span
      className={cn(
        'flex items-center gap-1 text-muted-foreground',
        classes.container,
        className
      )}
    >
      {icon && (
        <span className={classes.icon}>
          {React.isValidElement(icon)
            ? React.cloneElement(icon as React.ReactElement<any>, {
                className: cn(classes.icon, (icon as any).props?.className),
              })
            : icon}
        </span>
      )}
      <span>
        {value}
        {label && ` ${label}`}
      </span>
    </span>
  );
}

/**
 * PropertyDetails - Container for multiple PropertyDetail components
 * 
 * @example
 * ```tsx
 * <PropertyDetails>
 *   <PropertyDetail icon={<Building2 />} value={50} label="rooms" />
 *   <PropertyDetail icon={<Users />} value={200} label="capacity" />
 * </PropertyDetails>
 * ```
 */
export interface PropertyDetailsProps {
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

export function PropertyDetails({ children, className }: PropertyDetailsProps) {
  return (
    <div className={cn('flex flex-wrap gap-3', className)}>
      {children}
    </div>
  );
}

